import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { 
  Calendar, Plus, Edit, Trash2, Wrench, Clock, User, Play, CheckCircle, 
  AlertCircle, Activity, Gauge, Settings, RotateCcw, RefreshCw, AlertTriangle, Package, Search
} from 'lucide-react';
import { DataTable } from '../../components/ui/data-table';
import { Combobox } from '../../components/ui/combobox';

const PredictiveMaintenancePage = () => {
  const navigate = useNavigate();
  const { plants, selectedPlant, locations, selectedLocation } = useData();
  const [revisions, setRevisions] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [users, setUsers] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [interventionTypes, setInterventionTypes] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  
  const emptyForm = {
    title: '',
    equipment_id: '',
    plant_id: '',
    location_id: '',
    scheduled_date: '',
    description: '',
    procedure_id: '',
    intervention_type_id: '',
    symptom_id: '',
    estimated_hours: '',
    assigned_to_ids: [],
    // Predictive specific fields
    counter_due: '',
    alert_before_counter: 10,
    repeat_revision: false,
    periodicity_counter: '',
    repeat_until_counter: ''
  };
  
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchData();
  }, [selectedPlant, selectedLocation]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { revision_type: 'predictive' };
      if (selectedPlant && selectedPlant !== 'all') params.plant_id = selectedPlant;
      if (selectedLocation && selectedLocation !== 'all') params.location_id = selectedLocation;
      
      const [revisionsRes, equipmentRes, usersRes, proceduresRes, typesRes, symptomsRes] = await Promise.all([
        api.get('/revisions', { params }),
        api.get('/equipment', { params: selectedPlant && selectedPlant !== 'all' ? { plant_id: selectedPlant } : {} }),
        api.get('/users'),
        api.get('/procedures'),
        api.get('/intervention-types'),
        api.get('/symptoms').catch(() => ({ data: [] }))
      ]);
      
      setRevisions(revisionsRes.data);
      setEquipment(equipmentRes.data);
      setUsers(usersRes.data.filter(u => ['technician', 'manager'].includes(u.role)));
      setProcedures(proceduresRes.data);
      setInterventionTypes(typesRes.data);
      setSymptoms(symptomsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const selectedEquip = equipment.find(eq => eq.id === formData.equipment_id);
      
      const payload = {
        ...formData,
        revision_type: 'predictive',
        equipment_name: selectedEquip?.name || '',
        plant_id: selectedEquip?.plant_id || formData.plant_id,
        location_id: selectedEquip?.location_id || formData.location_id,
        scheduled_date: formData.scheduled_date ? new Date(formData.scheduled_date).toISOString() : new Date().toISOString(),
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        counter_due: formData.counter_due ? parseInt(formData.counter_due) : null,
        alert_before_counter: parseInt(formData.alert_before_counter) || 10,
        periodicity_counter: formData.periodicity_counter ? parseInt(formData.periodicity_counter) : null,
        repeat_until_counter: formData.repeat_until_counter ? parseInt(formData.repeat_until_counter) : null,
        tasks: [],
        consumables: []
      };
      
      if (editItem) {
        await api.put(`/revisions/${editItem.id}`, payload);
        toast.success('Revizie actualizata!');
      } else {
        await api.post('/revisions', payload);
        toast.success('Revizie predictiva creata!');
      }
      
      setShowAddDialog(false);
      setEditItem(null);
      setFormData(emptyForm);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      title: item.title || '',
      equipment_id: item.equipment_id || '',
      plant_id: item.plant_id || '',
      location_id: item.location_id || '',
      scheduled_date: item.scheduled_date ? item.scheduled_date.split('T')[0] : '',
      description: item.description || '',
      procedure_id: item.procedure_id || '',
      intervention_type_id: item.intervention_type_id || '',
      symptom_id: item.symptom_id || '',
      estimated_hours: item.estimated_hours || '',
      assigned_to_ids: item.assigned_to_ids || (item.assigned_to ? [item.assigned_to] : []),
      counter_due: item.counter_due || '',
      alert_before_counter: item.alert_before_counter || 10,
      repeat_revision: item.repeat_revision || false,
      periodicity_counter: item.periodicity_counter || '',
      repeat_until_counter: item.repeat_until_counter || ''
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa stergeti aceasta revizie?')) return;
    try {
      await api.delete(`/revisions/${id}`);
      toast.success('Revizie stearsa!');
      fetchData();
    } catch (error) {
      toast.error('Eroare la stergere');
    }
  };

  const handleStatusChange = async (revisionId, newStatus) => {
    try {
      await api.put(`/revisions/${revisionId}`, { status: newStatus });
      toast.success('Status actualizat!');
      fetchData();
    } catch (error) {
      toast.error('Eroare la actualizare status');
    }
  };

  const toggleTechnician = (userId) => {
    if (formData.assigned_to_ids.includes(userId)) {
      setFormData({ ...formData, assigned_to_ids: formData.assigned_to_ids.filter(id => id !== userId) });
    } else {
      setFormData({ ...formData, assigned_to_ids: [...formData.assigned_to_ids, userId] });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      planned: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-purple-100 text-purple-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-slate-100 text-slate-700',
      waiting_parts: 'bg-orange-100 text-orange-700'
    };
    const labels = {
      planned: 'Planificat',
      in_progress: 'In lucru',
      completed: 'Finalizat',
      cancelled: 'Anulat',
      waiting_parts: 'Asteapta piese'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{labels[status] || status}</Badge>;
  };

  const getCounterProgress = (row) => {
    if (!row.counter_due) return null;
    const currentCounter = row.current_counter || 0;
    const progress = Math.min((currentCounter / row.counter_due) * 100, 100);
    const isNearDue = progress >= 80;
    const isDue = progress >= 100;
    
    return (
      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span>{currentCounter}</span>
          <span>{row.counter_due}</span>
        </div>
        <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all ${isDue ? 'bg-red-500' : isNearDue ? 'bg-orange-500' : 'bg-green-500'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    );
  };

  const tableColumns = [
    {
      key: 'title',
      label: 'Nume Revizie',
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium flex items-center gap-2">
            <Gauge className="w-4 h-4 text-cyan-500" />
            {value}
          </div>
          {row.repeat_revision && (
            <Badge variant="outline" className="mt-1 text-xs">
              <RotateCcw className="w-3 h-3 mr-1" />
              Recurent
            </Badge>
          )}
        </div>
      )
    },
    {
      key: 'equipment_name',
      label: 'Echipament',
      sortable: true,
      filterable: true
    },
    {
      key: 'counter_due',
      label: 'Contor Scadent',
      sortable: true,
      render: (value, row) => row ? getCounterProgress(row) : '-'
    },
    {
      key: 'periodicity_counter',
      label: 'Periodicitate',
      sortable: true,
      render: (value, row) => row?.repeat_revision ? `La ${value || '-'} unitati` : '-'
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterOptions: [
        { value: 'planned', label: 'Planificat' },
        { value: 'in_progress', label: 'In lucru' },
        { value: 'completed', label: 'Finalizat' }
      ],
      render: (value, row) => row ? getStatusBadge(value) : '-'
    },
    {
      key: 'assigned_to_name',
      label: 'Responsabil',
      sortable: true,
      filterable: true,
      render: (value, row) => {
        if (row?.assigned_to_ids?.length > 1) {
          return <Badge variant="outline">{row.assigned_to_ids.length} tehnicieni</Badge>;
        }
        return value || '-';
      }
    },
    {
      key: 'actions',
      label: 'Actiuni',
      sortable: false,
      render: (_, row) => row ? (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          {row.status === 'planned' && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleStatusChange(row.id, 'in_progress')}
              className="text-purple-600"
            >
              <Play className="w-4 h-4" />
            </Button>
          )}
          {row.status === 'in_progress' && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleStatusChange(row.id, 'completed')}
              className="text-green-600"
            >
              <CheckCircle className="w-4 h-4" />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => handleEdit(row)}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} className="text-red-600">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ) : null
    }
  ];

  // Stats
  const stats = {
    total: revisions.length,
    planned: revisions.filter(r => r.status === 'planned').length,
    inProgress: revisions.filter(r => r.status === 'in_progress').length,
    nearDue: revisions.filter(r => {
      if (!r.counter_due || !r.current_counter) return false;
      return (r.current_counter / r.counter_due) >= 0.8;
    }).length
  };

  return (
    <div className="space-y-6" data-testid="predictive-maintenance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Mentenanta Predictiva</h1>
          <p className="text-slate-600 mt-1">Revizii bazate pe contor si utilizare echipament</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="h-11">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizeaza
          </Button>
          <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) { setEditItem(null); setFormData(emptyForm); } }}>
            <DialogTrigger asChild>
              <Button className="btn-primary h-11">
                <Plus className="w-5 h-5 mr-2" />
                REVIZIE NOUA
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editItem ? 'Editeaza Revizie Predictiva' : 'Adauga Revizie Predictiva'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Basic Info */}
                <div className="space-y-2">
                  <Label>Nume revizie *</Label>
                  <Input 
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="h-11"
                    placeholder="Ex: Schimb ulei la 10000 km"
                  />
                  <p className="text-xs text-slate-500">
                    Daca selectati repetare, numele va fi completat cu numarul perioadei
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Echipament *</Label>
                    <Combobox
                      items={equipment}
                      value={formData.equipment_id}
                      onSelect={(id) => setFormData({ ...formData, equipment_id: id })}
                      placeholder="Selecteaza echipamentul..."
                      searchPlaceholder="Cauta echipament..."
                      emptyText="Niciun echipament gasit."
                      codeKey="code"
                      secondaryKey="location_name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tip interventie</Label>
                    <Combobox
                      items={interventionTypes}
                      value={formData.intervention_type_id}
                      onSelect={(id) => setFormData({ ...formData, intervention_type_id: id })}
                      placeholder="Selecteaza tipul..."
                      searchPlaceholder="Cauta tip interventie..."
                      emptyText="Niciun tip gasit."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Simptom trigger</Label>
                    <Combobox
                      items={symptoms}
                      value={formData.symptom_id}
                      onSelect={(id) => setFormData({ ...formData, symptom_id: id })}
                      placeholder="Selecteaza simptomul..."
                      searchPlaceholder="Cauta simptom..."
                      emptyText="Niciun simptom gasit."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Procedura</Label>
                    <Combobox
                      items={procedures}
                      value={formData.procedure_id}
                      onSelect={(id) => setFormData({ ...formData, procedure_id: id })}
                      placeholder="Selecteaza procedura..."
                      searchPlaceholder="Cauta procedura..."
                      emptyText="Niciun procedura gasita."
                      nameKey="name"
                      descKey="descriere"
                      codeKey="cod"
                    />
                  </div>
                </div>

                <Separator />

                {/* Counter Settings */}
                <div className="bg-cyan-50 p-4 rounded-lg space-y-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <Gauge className="w-5 h-5 text-cyan-600" />
                    Setari Contor
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Contor scadent revizie *</Label>
                      <Input 
                        type="number"
                        value={formData.counter_due}
                        onChange={(e) => setFormData({ ...formData, counter_due: e.target.value })}
                        className="h-11"
                        placeholder="200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Alerta inainte cu (unitati)</Label>
                      <Input 
                        type="number"
                        value={formData.alert_before_counter}
                        onChange={(e) => setFormData({ ...formData, alert_before_counter: e.target.value })}
                        className="h-11"
                        placeholder="10"
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Repeta revizia/interventia</Label>
                      <p className="text-sm text-slate-500">Genereaza automat revizii la interval de contor</p>
                    </div>
                    <Switch 
                      checked={formData.repeat_revision}
                      onCheckedChange={(checked) => setFormData({ ...formData, repeat_revision: checked })}
                    />
                  </div>

                  {formData.repeat_revision && (
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div className="space-y-2">
                        <Label>Periodicitate contor *</Label>
                        <Input 
                          type="number"
                          value={formData.periodicity_counter}
                          onChange={(e) => setFormData({ ...formData, periodicity_counter: e.target.value })}
                          className="h-11"
                          placeholder="1000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Repeta pana la contor</Label>
                        <Input 
                          type="number"
                          value={formData.repeat_until_counter}
                          onChange={(e) => setFormData({ ...formData, repeat_until_counter: e.target.value })}
                          className="h-11"
                          placeholder="50000"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Assignment */}
                <div className="space-y-3">
                  <Label>Tehnicieni responsabili (selecteaza unul sau mai multi)</Label>
                  <div className="flex flex-wrap gap-2">
                    {users.map(user => (
                      <Badge 
                        key={user.id}
                        variant={formData.assigned_to_ids.includes(user.id) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => toggleTechnician(user.id)}
                      >
                        <User className="w-3 h-3 mr-1" />
                        {user.full_name}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data planificata</Label>
                    <Input 
                      type="date"
                      value={formData.scheduled_date}
                      onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ore estimate</Label>
                    <Input 
                      type="number"
                      step="0.5"
                      value={formData.estimated_hours}
                      onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })}
                      className="h-11"
                      placeholder="2"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Descriere</Label>
                  <Textarea 
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder="Detalii despre revizie..."
                  />
                </div>

                <div className="bg-amber-50 p-3 rounded-lg">
                  <p className="text-xs text-amber-700">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    In cadrul repetarii reviziei/interventiei nu se adauga automat consumabilele si documentele de la inregistrarea curenta!
                  </p>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Inapoi</Button>
                  <Button type="submit" className="btn-primary">Salveaza</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                <Gauge className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-slate-600">Total revizii</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.planned}</p>
                <p className="text-sm text-slate-600">Planificate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Play className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.inProgress}</p>
                <p className="text-sm text-slate-600">In lucru</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-industrial">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.nearDue}</p>
                <p className="text-sm text-slate-600">Aproape scadente</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Data Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
        </div>
      ) : (
        <DataTable 
          data={revisions} 
          columns={tableColumns}
          pageSize={10}
          searchable={true}
          exportable={true}
          printable={true}
          onRowClick={(row) => navigate(`/revisions/${row.id}`)}
          emptyMessage="Nu exista revizii predictive. Creati prima revizie!"
        />
      )}
    </div>
  );
};

export default PredictiveMaintenancePage;
